import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, TextField } from "@mui/material";

import { apiClient } from "../../../api/httpClient";
import {
  getPrincipalPickerConfig,
  principalDisplayName,
} from "./endorsementPrincipalPickerConfig";

/** Principal name change — optional picker when multiple principals exist. */
export default function EndorsementPrincipalPickerFields({
  selectedBondToEndorse,
  updatedName,
  setUpdatedName,
  endorsementPrincipalId,
  setEndorsementPrincipalId,
}) {
  const bondType = selectedBondToEndorse?.bond_type;
  const config = getPrincipalPickerConfig(bondType);
  const [principals, setPrincipals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const bondId = selectedBondToEndorse?.bond_id;
    if (!bondId || !config) {
      setPrincipals([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const data = await apiClient(config.retrieve(bondId));
        const list = data?.[config.listKey]?.length ? data[config.listKey] : [];
        if (!cancelled) {
          setPrincipals(list);
          if (list.length >= 1 && !endorsementPrincipalId) {
            setEndorsementPrincipalId(String(list[0][config.idField]));
          }
        }
      } catch {
        if (!cancelled) setPrincipals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBondToEndorse?.bond_id, bondType, setEndorsementPrincipalId]);

  if (!config) {
    return (
      <TextField
        label="Updated Name"
        value={updatedName}
        onChange={(e) => setUpdatedName(e.target.value)}
        fullWidth
        required
        sx={{ mb: 3 }}
      />
    );
  }

  const defaultPrincipalId =
    principals.length >= 1 ? String(principals[0][config.idField]) : "";
  const selectValue = endorsementPrincipalId || defaultPrincipalId;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
      {principals.length > 1 && (
        <TextField
          select
          label={config.pickerLabel}
          value={selectValue}
          onChange={(e) => setEndorsementPrincipalId(e.target.value)}
          fullWidth
          required
          disabled={loading}
          slotProps={{
            select: { native: true },
            inputLabel: { shrink: true },
            input: {
              endAdornment: loading ? (
                <CircularProgress size={18} sx={{ mr: 2 }} />
              ) : null,
            },
          }}
        >
          {principals.map((row) => (
            <option
              key={row[config.idField]}
              value={String(row[config.idField])}
            >
              {principalDisplayName(row, config)}
            </option>
          ))}
        </TextField>
      )}
      <TextField
        label="Updated Name"
        value={updatedName}
        onChange={(e) => setUpdatedName(e.target.value)}
        fullWidth
        required
      />
    </Box>
  );
}

EndorsementPrincipalPickerFields.propTypes = {
  selectedBondToEndorse: PropTypes.object,
  updatedName: PropTypes.string,
  setUpdatedName: PropTypes.func.isRequired,
  endorsementPrincipalId: PropTypes.string,
  setEndorsementPrincipalId: PropTypes.func.isRequired,
};
